import React from "react";
import { addEllipsisToText } from "../../../utils/utils";
import { convertToIST } from "../agentUtils";

// Mapping subStatus to display text
const statusMapping = [
  { combination: ["FJ"], status: "Pending Customer Consent" },
  { combination: ["YO"], status: "Pending for consent on revised BI & Revised Proposal form" },
  { combination: ["VV"], status: "Pending PIVC" },
  { combination: ["MP"], status: "Pending Medical Appointment" },
  { combination: ["NP"], status: "Pending Non Medical Document" },
  { combination: ["AI"], status: "Resubmit Pending Documents" },
  { combination: ["CO"], status: "Awaited acceptance on Counter Offer" },
  { combination: ["PM"], status: "UW Accepted" },
  { combination: ["PA"], status: "Payment awaited" },
  { combination: ["CB"], status: "Payment awaited (due to Cheque Dishonor)" },
  { combination: ["NM"], status: "Pending Medical & Non Medical" },
  { combination: ["AS"], status: "Form filling In Progress" },
  { combination: ["UD"], status: "Form filling Completed" },
  { combination: ["DC"], status: "Pending for QC" },
  { combination: ["AR"], status: "Discrepancy raised" },
  { combination: ["MA"], status: "Pending for Medical Appointment" },
  { combination: ["PB"], status: "Pending for internal decision" },
  { combination: ["RA"], status: "Pending for UW" },
  { combination: ["IP"], status: "Approved" },
  { combination: ["PI"], status: "Issued" },
  { combination: ["PD"], status: "Declined" },
  { combination: ["PP"], status: "Postponed" },
  { combination: ["PR"], status: "Rejected" },
  { combination: ["PC"], status: "Cancelled" },
  { combination: ["RR"], status: "Requirement Under Review" },
];

// CHANGED: Non-terminal vs terminal subStatus classification for QC/Policy header logic.
// Non-terminal (DC, IP, RR, etc.) compete in nonTerminalPool by actual_status + updatedOn.
// Terminal (PA, PM, CO, NM, etc.) are handled in IN-PROGRESS blocks above; when COMPLETED,
// they can suppress completed DC from the pool (see isAnyTerminalCompleted).
const NON_TERMINAL_SUB_STATUSES = ["DC", "RR",'RA', "PB", "AS", "UD", "IP","PI", "PD", "PP", "PR", "PC"];
const TERMINAL_SUB_STATUSES = ["CO", "AI", "AR", "NP", "NM", "PA", "PM", "CB", "MP"];

// Generic function to find the status with the latest `updatedOn`
const getLatestStatus = (statuses) => {
  return statuses.reduce((latest, current) => {
    const latestDate = new Date(latest?.updatedOn || 0);
    const currentDate = new Date(current?.updatedOn || 0);
    return currentDate > latestDate ? current : latest;
  }, {});
};

const getStatusFromSubStatus = (subStatus) =>
  statusMapping.find((item) => item.combination.includes(subStatus));

const HeaderStatus = ({ bannerDetail, accordionListDetails, toggleAccordion, consentAccepted,customerDetail,hasDiscrepancy,hasDiscrepancyRaised }) => { 
  const findItem = (status) =>
    accordionListDetails?.find((item) => item.status === status);

  const findSubStatusByCondition = (conditionFn) =>
    accordionListDetails?.find(conditionFn)?.subStatus;

  const quote = findItem("QUOTE");
  const quote_subStatus = quote?.subStatus;
  const quote_status = getStatusFromSubStatus(quote_subStatus);
  const quote_updateOn = new Date(quote?.updatedOn || 0);

  const otherInProgress = accordionListDetails?.filter(
    (item) => item.status !== "QUOTE" && item.actual_status === "IN-PROGRESS" && item.subStatus
  );

  const latestOther = otherInProgress?.reduce((latest, current) =>
    new Date(current.updatedOn || 0) > new Date(latest?.updatedOn || 0) ? current : latest, null);

  const medical = findItem("MEDICAL_REQUIREMENT");
  const medical_status = getStatusFromSubStatus(medical?.subStatus);

  const addInfo = findItem("ADDITIONAL_NON_MEDICAL_REQUIREMENTS");
  const addInfo_status = getStatusFromSubStatus(addInfo?.subStatus);

  const co = findItem("REVISED_OFFER");
  const co_status = getStatusFromSubStatus(co?.subStatus);

  const payment = findItem("PAYMENT_REQUIREMENT");
  const payment_status = getStatusFromSubStatus(payment?.subStatus);
  const payment_updateOn = new Date(payment?.updatedOn || 0);

  const quality = findItem("QUALITY_CHECK");
  const quality_status = getStatusFromSubStatus(quality?.subStatus);

  const policy = findItem("POLICY_STATUS");
  const policy_status = getStatusFromSubStatus(policy?.subStatus);
  const policy_updateOn = new Date(policy?.updatedOn || 0);

  const instaVerify = accordionListDetails?.find((item) => item.subStatus === "INSTA_VERIFY_STATUS");
  const pivcStatus = instaVerify?.additionalInfo?.instaVerify?.pivcStatus;
  const pivcRemarks = instaVerify?.additionalInfo?.instaVerify?.remarks;
  const PivcRetrigger = instaVerify?.additionalInfo?.instaVerify?.status === 3; // status 3 = customer disagreed, retrigger needed

  const isInstaRequired = instaVerify?.additionalInfo?.isInstaRequired;
  const pivcCompleteFlag = instaVerify?.additionalInfo?.instaVerify?.PIVCCompleteFlag;

  const subStatusInProgress = findSubStatusByCondition(
    (item) => item.actual_status === "IN-PROGRESS"
  );
  const other_status = getStatusFromSubStatus(subStatusInProgress);

  const isInsta = bannerDetail?.instaFlag === "INSTA";
  const hasPolicy = !!bannerDetail?.policyNumber?.trim();

  const consentStatusCode = bannerDetail?.consentDetail?.consentStatusCode === 'FJ';
  const isParallelConsentTriggeredOnPfSubmit = bannerDetail?.consentDetail?.isParallelConsentTriggeredOnPfSubmit;
  const fjConsentRaisedByNgRequest =bannerDetail?.consentDetail?.fjConsentRaisedByNgRequest;
  const isNbqcChanges =bannerDetail?.consentDetail?.isNbqcChanges;
  const isConsentPending = consentAccepted !== true; // handles null/undefined
  // CHANGED: was incorrectly checking COMPLETED; parallel consent needs DC while IN-PROGRESS.
  const isQCInProgress = quality?.subStatus === "DC" && quality?.actual_status === "IN-PROGRESS";
  const isDiscrepancyRaised = addInfo?.subStatus === "AR" && addInfo?.actual_status === "IN-PROGRESS";
  const isDiscrepancyResolved = addInfo?.actual_status === "COMPLETED";

  // Aligned with AgentView; skip PIVC when consent accepted and FJ raised by NG — show requirement status only
  const isPivcPending =
    isInstaRequired &&
    !PivcRetrigger &&
    pivcCompleteFlag !== "Yes" &&
    !["APPROVED", "PARKED", "UnAssigned"].includes(pivcStatus) &&
    !(consentAccepted === true && fjConsentRaisedByNgRequest);

  // console.log('consentStatusCode',consentStatusCode,'\n',
  //   'consentAccepted', consentAccepted, '\n',
  //   'isParallelConsentTriggeredOnPfSubmit', isParallelConsentTriggeredOnPfSubmit,'\n',
  //   'fjConsentRaisedByNgRequest', fjConsentRaisedByNgRequest, '\n', 
  //   'isNbqcChanges', isNbqcChanges, '\n','\n',
  //   'isConsentPending', isConsentPending, '\n',
  //   'isQCInProgress', isQCInProgress, '\n',
  //   'isDiscrepancyRaised', isDiscrepancyRaised, '\n',
  //   'isDiscrepancyResolved', isDiscrepancyResolved,'\n','\n',
  //   'hasDiscrepancy', hasDiscrepancy, '\n',
  //   'hasDiscrepancyRaised', hasDiscrepancyRaised, '\n'
  // );

    // Get currentChildWS from QUOTE status item
    let currentChildWS = [];
    if (accordionListDetails && accordionListDetails?.length) {
      const firstAvailable = accordionListDetails?.find(
        (item) => item?.status === 'QUOTE'
      );
      currentChildWS = firstAvailable?.currentChildWS || [];
    }

    const hasIVC = currentChildWS?.some(
      (ws) => typeof ws === "string" && ws.trim().toUpperCase() === "IVC"
    );

    // If "IVC" found anywhere in currentChildWS, use comma separator
    const newStatus = currentChildWS?.length
      ? (hasIVC
        ? ", " + currentChildWS.join(", ").trim()
        : " - " + currentChildWS.join(", ").trim())
      : "";

    const renderStatus = (text, className = 'header-link-text') => {
    // Append PIVC when Insta Verify is incomplete (NGT-34829 / OBI-8449)
    const isPivcApplicable = isPivcPending;
    // unmapped in-progress subStatuses (e.g. Personal_Details) can pass undefined as text
    const safeText = text || "Form filling In Progress";
    const alreadyHasPivc = /pending\s*(for\s*)?pivc/i.test(safeText);
    let finalText = isPivcApplicable && !alreadyHasPivc
      ? `${safeText}, Pending for PIVC`
      : safeText;

    // PAYMENT SPECIAL LOGIC WHEN subStatus = PA
    if (payment?.subStatus === "PA") {
      const history = payment?.history || [];
      const paymentTriggered = history
        .filter(h => h.entityStatus === "PAYMENT_TRIGGERED")
        .sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn))[0];

      const paymentTriggeredDate = paymentTriggered?.createdOn
        ? renderDate(paymentTriggered.createdOn)
        : null;

      // Case 1 — Triggered but NOT completed yet
      if (payment?.actual_status === "IN-PROGRESS") {
        const updatedText = paymentTriggeredDate
          ? `Payment Awaited – Triggered On: ${paymentTriggeredDate}`
          : "Payment Awaited";
        finalText = updatedText;
      }

      // Case 2 — Payment Completed
      if (payment?.actual_status === "COMPLETED" && payment_updateOn < policy_updateOn) {
        const offlineMethods = ["DEMAND_DRAFT", "CASH", "NEFT", "CHEQUE"];
        const hasOfflinePayment =
          bannerDetail?.methodOfPayments?.some(m => offlineMethods.includes(m.paymentMethod));

        if (hasOfflinePayment) {
          return (
            <div className={className}>
              Status: Physical instrument/cash not handed over to Branch ops for receipting
            </div>
          );
        }

        const paymentCompletedDate = payment?.updatedOn
          ? renderDate(payment?.updatedOn)
          : null;

        const updatedText = `Payment Completed On: ${paymentCompletedDate}`;
        finalText = updatedText;
      }
    }
   
    return (
      <>
        <div className="status-text-container">
          {PivcRetrigger ? <span>Status: PIVC disagree by Customer</span> : <span>
            Status: {addEllipsisToText(finalText)}
            {newStatus}
          </span>
        }

        {/* Non-parallel flow only: consent is sent after NBQC, not at PF submit (NGT-34829) */}
        {finalText.startsWith('Pending for QC') &&
          !isParallelConsentTriggeredOnPfSubmit &&
          consentAccepted !== true && (
          <div style={{ fontSize: '12px', marginTop: '4px' }}>
          Important: Customer Consent will be sent after successful NBQC.
        </div>)}
        </div>

      {/* NGT-35605: show PIVC retrigger remarks below status */}
      {PivcRetrigger &&
       <div className="header-remarks">
 Remarks: {pivcRemarks}
      </div>
      }
      </>
    );
  };

  const renderHeaderBlk = (text) => (
    <div className="header-blk">
      {renderStatus(text)}
    </div>
  );

  const renderDate = (date) => {
    const istDate = convertToIST(date, false, true);
    return istDate;
  };


  // Special handling for YO
  if (quote_subStatus === "YO") {
    if (!latestOther || quote_updateOn >= new Date(latestOther?.updatedOn || 0)) {
      return <>{renderStatus(quote_status?.status)}</>;
    }
    const latestStatus = getStatusFromSubStatus(latestOther?.subStatus);
    return <>{renderStatus(latestStatus?.status)}</>;
  }

    // FJ handling — non-parallel consent flow (consent sent only after PIVC clearance)
 if (quote_subStatus === "FJ" && consentAccepted===null && !isParallelConsentTriggeredOnPfSubmit) {
  if (!isInstaRequired) return renderHeaderBlk(quote_status?.status);
    if (PivcRetrigger) {
      return renderHeaderBlk('PIVC disagree by Customer');
    }
    if (["APPROVED", "PARKED", "UnAssigned"].includes(pivcStatus)) {
      return renderHeaderBlk(quote_status?.status);
    }
    // if(isParallelConsentTriggeredOnPfSubmit ) {
    // return renderHeaderBlk("Pending for PIVC" +",  "+"Pending Customer Consent");
    // }
    //  if(isParallelConsentTriggeredOnPfSubmit && isNbqcChanges) {
    // return renderHeaderBlk("Pending for PIVC" +",  "+"Pending Customer Consent");
    // }
    return renderHeaderBlk("Pending for PIVC");
  }
  
    // parallel consent handling (NGT-34829)---------
  // Case 1: DC + pending consent — merge QC status with consent; child WS appended via newStatus in renderStatus
  if (
    isParallelConsentTriggeredOnPfSubmit &&
    isQCInProgress && !hasDiscrepancy &&
    consentStatusCode && !isNbqcChanges
  ) {
    if (consentAccepted === null) {
      return renderHeaderBlk(
        `${quality_status?.status}, Pending Customer Consent`
      );
    }
    // Consent actioned — show QC only; PIVC appended by renderStatus if still pending
    return renderHeaderBlk(`${quality_status?.status}`);
  }

  // Case 2: Discrepancy raised + pending consent
  if (
    isParallelConsentTriggeredOnPfSubmit &&
    hasDiscrepancyRaised &&
    consentStatusCode
    && isConsentPending
  ) {
    return renderHeaderBlk(
      `${addInfo_status?.status}, Pending Customer Consent`
    );
  }
  // Case 2b: discrepancy resolved, consent still pending (FJ re-raised after requirements closed)
  if (
    isParallelConsentTriggeredOnPfSubmit &&
    isDiscrepancyResolved &&
    consentStatusCode
    && isConsentPending && !isNbqcChanges
  ) {
    return renderHeaderBlk(
      `Pending Customer Consent`
    );
  }

  // Case 3: isNbqcChanges=YES — revised consent; include PIVC when Insta Verify incomplete
  if(isParallelConsentTriggeredOnPfSubmit && isNbqcChanges && consentStatusCode && !consentAccepted) {
    if (isPivcPending) {
      return renderHeaderBlk(
        `Pending for PIVC, Pending Revised Customer Consent`
      );
    }
    return renderHeaderBlk("Pending Revised Customer Consent"); 
  }

  // Parallel consent fallback: consent sent at PF submit, pending action, QC/discrepancy not started yet
  if (
    isParallelConsentTriggeredOnPfSubmit &&
    consentStatusCode &&
    consentAccepted === null &&
    !isNbqcChanges
  ) {
    return renderHeaderBlk("Pending Customer Consent");
  }

  // Consent done, Insta Verify still incomplete, QC/requirements not started yet
  if (isParallelConsentTriggeredOnPfSubmit && isPivcPending) {
    return renderHeaderBlk("Pending for PIVC");
  }



  // Handling the special cases for DC and RA, PB, MA, RR
  //  const isRRCompleted =
  //  ( policy_status === "RR" || policy_status === "RA") && policy?.actual_status === "COMPLETED";
  //   const qualityAndPolicyStatus = [quality, policy].filter(item =>
  //   item && item.subStatus &&
  //   (
  //     isRRCompleted
  //       ? item === policy                 // only policy RR (COMPLETED)
  //       : item.actual_status === "IN-PROGRESS"
  //   )
  // );


  // const qualityAndPolicyStatus = [
  //   quality,
  //   policy
  // ].filter(item => item && item.subStatus );
  // const latestStatusToShow = getLatestStatus(qualityAndPolicyStatus);
  // if (latestStatusToShow && Object.keys(latestStatusToShow).length > 0) {
  //   const latestStatus = getStatusFromSubStatus(latestStatusToShow?.subStatus);
  //   return renderStatus(latestStatus?.status);
  // }
  
  // Insta flow
  if (isInsta) {
    if (!hasPolicy && (quote_subStatus !== "FJ" || payment?.subStatus !== "PA")) {
      return renderHeaderBlk("Form filling In Progress");
    }
    if (quote_subStatus === "FJ" && quote?.actual_status === "IN-PROGRESS") {
      return renderStatus(quote_status?.status, "header-link");
    }
    if (["PA", "CB"].includes(payment?.subStatus) && payment?.actual_status === "IN-PROGRESS") {
      return renderStatus(payment_status?.status, "header-link");
    }
  } else {
    // Medical
    if (["NM", "MP"].includes(medical?.subStatus) && medical?.actual_status === "IN-PROGRESS") {
      return renderStatus(medical_status?.status, "header-link");
    }

    // Additional Info
    if (addInfo?.actual_status === "IN-PROGRESS") {
      if (addInfo?.subStatus === "AR") {
        return (
          <div className="header-blk">
            <div className="header-hover">{addInfo_status?.status}</div>
            {renderStatus(addInfo_status?.status)}
          </div>
        );
      }

      if (["AI", "NP"].includes(addInfo?.subStatus)) {
        return (
          <div className="header-container">
            <div className="header-hover">{addInfo_status?.status}</div>
            {renderStatus(addInfo_status?.status, "header-link")}
          </div>
        );
      }
    }

    // Counter Offer
    if (co?.subStatus === "CO" && co?.actual_status === "IN-PROGRESS") {
      return renderStatus(co_status?.status, "header-link");
    }

    // Payment
    if (["PA", "PM", "CB"].includes(payment?.subStatus) && payment?.actual_status === "IN-PROGRESS") {
      return renderStatus(payment_status?.status, "header-link");
    }

    // --QUALITY CHECK AND POLICY STATUS HANDLING--

    const isAnyTerminalCompleted = [payment, medical, addInfo, co].some(
      (item) =>
        item?.subStatus &&
        TERMINAL_SUB_STATUSES.includes(item.subStatus) &&
        item.actual_status === "COMPLETED"
    );

    const isDCCompleted =
      quality?.subStatus === "DC" &&
      quality?.actual_status === "COMPLETED";

    const isRRCompleted =
      policy?.subStatus === "RR" && policy?.actual_status === "COMPLETED";

    // Eligible non-terminal items only; excludes completed DC after terminal step done.
    const nonTerminalPool = [quality, policy].filter((item) => {
      if (!item?.subStatus) return false;
      if (isRRCompleted && item !== policy) return false;
      if (!NON_TERMINAL_SUB_STATUSES.includes(item.subStatus)) return false;
      if (!["IN-PROGRESS", "COMPLETED"].includes(item.actual_status)) return false;
      // Suppress DC when e.g. PM/PA/CO/NM completed and BE marked DC completed.
      if (item.subStatus === "DC" && isAnyTerminalCompleted && isDCCompleted) return false;
      return true;
    });

    // IN-PROGRESS first, then most recent updatedOn among same state.
    const latestNonTerminal = nonTerminalPool.length > 0
      ? [...nonTerminalPool].sort((a, b) => {
        if (a.actual_status === "IN-PROGRESS" && b.actual_status === "COMPLETED") return -1;
        if (a.actual_status === "COMPLETED" && b.actual_status === "IN-PROGRESS") return 1;
        return new Date(b.updatedOn || 0) - new Date(a.updatedOn || 0);
      })[0]
      : null;

    if (latestNonTerminal?.subStatus) {
      const latestStatus = getStatusFromSubStatus(latestNonTerminal.subStatus);
      return renderStatus(latestStatus?.status);
    }

    // Policy Cancelled
    if (policy?.subStatus === "PC" && policy?.actual_status === "COMPLETED") {
      return renderStatus(policy_status?.status);
    }

    // Policy other statuses
    if (["RA", "PB", "MA"].includes(policy?.subStatus)) {
      return renderStatus(policy_status?.status);
    }

    // Quote status fallback
    if (["AS", "UD", "YO"].includes(quote_subStatus) && !consentAccepted) {
      return renderStatus(quote_status?.status);
    }

    if (quote_subStatus === "YO" && consentAccepted) {
      return renderStatus(quote_status?.status, "header-link");
    }

    if (quote_subStatus === "VV" && !consentAccepted) {
      return renderStatus(quote_status?.status, "header-link");
    }

    // PROPOSAL subStatuses (e.g. Personal_Details) are not in statusMapping — skip if unmapped
    if (subStatusInProgress && other_status?.status) {
      return renderStatus(other_status.status, "header-link");
    }
    // Fallback when form filling in progress and no policy number yet
    if (!hasPolicy) {
      return renderHeaderBlk("Form filling In Progress");
    }

  }
  return null;
};

export default HeaderStatus;